import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
  IconButton,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { batchImportService } from '@/services/receipts/batch-import.service';
import { useNotification } from '@/contexts/NotificationContext';
import { eventBus } from '@/utils/eventBus';

const MAX_FILES = 20;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

interface ReceiptBatchImportDialogProps {
  open: boolean;
  onClose: () => void;
  customerId?: string;
}

export const ReceiptBatchImportDialog: React.FC<ReceiptBatchImportDialogProps> = ({
  open: openProp,
  onClose,
  customerId,
}) => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [internalOpen, setInternalOpen] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [eventCustomerId, setEventCustomerId] = useState<string | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const open = internalOpen || openProp;
  const activeCustomerId = customerId || eventCustomerId;

  const openWithJobResults = useCallback(async (jobId: string) => {
    const jobStatus = await batchImportService.getJobStatus(jobId);

    if (jobStatus.success && jobStatus.data?.customer?.id) {
      navigate(`/empresa/${jobStatus.data.customer.id}/comprovantes/importacao-lote?jobId=${jobId}`);
    } else {
      navigate(`/comprovantes/importacao-lote?jobId=${jobId}`);
    }
  }, [navigate]);

  useEffect(() => {
    const unsubscribe = eventBus.on('batch_import:open', (data) => {
      const eventData = data as { jobId: string } | undefined;
      if (eventData?.jobId) {
        openWithJobResults(eventData.jobId);
      }
    });

    return unsubscribe;
  }, [openWithJobResults]);

  useEffect(() => {
    const unsubscribe = eventBus.on('batch_import:open_upload', (data) => {
      const eventData = data as { customerId?: string } | undefined;
      setEventCustomerId(eventData?.customerId);
      setInternalOpen(true);
    });

    return unsubscribe;
  }, []);

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return `"${file.name}" - Tipo não suportado. Use PDF, JPEG ou PNG.`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `"${file.name}" - Arquivo muito grande. Máximo 10MB.`;
    }
    return null;
  };

  const handleFilesSelected = useCallback(
    (newFiles: FileList | File[]) => {
      setError('');
      const fileArray = Array.from(newFiles);
      const currentCount = files.length;

      if (currentCount + fileArray.length > MAX_FILES) {
        setError(`Máximo de ${MAX_FILES} arquivos. Você tem ${currentCount} e tentou adicionar ${fileArray.length}.`);
        return;
      }

      const errors: string[] = [];
      const validFiles: File[] = [];

      for (const file of fileArray) {
        const validationError = validateFile(file);
        if (validationError) {
          errors.push(validationError);
        } else {
          if (files.some((f) => f.name === file.name && f.size === file.size)) {
            errors.push(`"${file.name}" - Arquivo duplicado.`);
          } else {
            validFiles.push(file);
          }
        }
      }

      if (errors.length > 0) {
        setError(errors.join('\n'));
      }

      if (validFiles.length > 0) {
        setFiles((prev) => [...prev, ...validFiles]);
      }
    },
    [files]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      handleFilesSelected(e.dataTransfer.files);
    },
    [handleFilesSelected]
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setError('');
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setUploading(true);
    setError('');

    const result = await batchImportService.uploadFiles(files, activeCustomerId);

    setUploading(false);

    if (result.success && result.jobId) {
      showNotification('Arquivos enviados! Você será notificado quando o processamento terminar.', 'success');
      handleClose();
    } else {
      setError(result.error || 'Erro ao enviar arquivos');
    }
  };

  const handleClose = () => {
    if (uploading) return;
    setFiles([]);
    setError('');
    setEventCustomerId(undefined);
    setInternalOpen(false);
    onClose();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontSize: '1.125rem', fontWeight: 600 }}>
          Importação em Lote de Comprovantes
        </Typography>
        <IconButton onClick={handleClose} disabled={uploading} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
            {error}
          </Alert>
        )}

        {/* Drop zone */}
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => !uploading && fileInputRef.current?.click()}
          sx={{
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            cursor: uploading ? 'default' : 'pointer',
            bgcolor: 'grey.50',
            transition: 'border-color 0.2s',
            '&:hover': uploading
              ? {}
              : {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                },
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
            Arraste arquivos aqui ou clique para selecionar
          </Typography>
          <Typography variant="body2" color="text.secondary">
            PDF, JPEG ou PNG - Máximo {MAX_FILES} arquivos, 10MB cada
          </Typography>
        </Box>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png"
          style={{ display: 'none' }}
          onChange={(e) => {
            if (e.target.files) {
              handleFilesSelected(e.target.files);
              e.target.value = '';
            }
          }}
        />

        {/* File list */}
        {files.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              {files.length} arquivo(s) selecionado(s)
            </Typography>
            <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
              {files.map((file, index) => (
                <ListItem
                  key={`${file.name}-${index}`}
                  sx={{ px: 1 }}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleRemoveFile(index)}
                      disabled={uploading}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <FileIcon fontSize="small" color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary={file.name}
                    secondary={formatFileSize(file.size)}
                    primaryTypographyProps={{ fontSize: '0.875rem', noWrap: true }}
                    secondaryTypographyProps={{ fontSize: '0.75rem' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Upload progress */}
        {uploading && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Enviando arquivos...
            </Typography>
            <LinearProgress />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant="outlined" color="inherit" disabled={uploading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={files.length === 0 || uploading}
        >
          {uploading ? 'Enviando...' : `Processar (${files.length})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
